import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { knowledgebaseService } from '../Services/knowledgebaseService';
import { Knowledgebase, KnowledgebaseFormData } from '../Types';
import { StoreI18n, StoreSettings } from '../Types/StoreTypes';
import { RootState, useAppDispatch, useAppSelector } from '../store';

export default function useKnowledgebase() {
    const { id } = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const i18n = useAppSelector((state: RootState) => (state.i18n as StoreI18n).i18n);
    const [isLoaded, setIsLoaded] = useState(!id || id === 'new');
    const [isSaving, setIsSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(id !== undefined && id !== 'new');
    const [knowledgebaseSnapshot, setKnowledgebaseSnapshot] = useState<Knowledgebase | null>(null);
    const [refreshCount, setRefreshCount] = useState<number>(0);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const { embeddingModelDlp } = useAppSelector((state: RootState) => state.settings as StoreSettings);

    const [formData, setFormData] = useState<KnowledgebaseFormData>({
        name: '',
        description: '',
        documents: [],
    });

    const handleChange = (name: string, value: string | File[]) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleReembedDocument = async (identifier: string) => {
        const result = await knowledgebaseService.reembedDocument(identifier);
        toast.success(i18n.translate({ id: result.data.success ? 'documentReembed.success' : 'documentReembed.error', sourceName: 'Knowledgebase' }));
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            let result;
            if (isEditMode && knowledgebaseSnapshot) {
                result = await knowledgebaseService.updateKnowledgebase(knowledgebaseSnapshot.identifier, formData);
            } else {
                result = await knowledgebaseService.createKnowledgebase(formData);
            }

            toast.success(i18n.translate({ id: 'toast.success', sourceName: 'Knowledgebase' }));
            if (id === 'new') {
                setIsLoaded(false);
                navigate('/admin/knowledgebases/' + result.data.identifier)
            } else {
                navigate('/admin/knowledgebases')
            }
        } catch (error) {
            toast.error(i18n.translate({ id: 'error', sourceName: 'Main' }));
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (id && id !== 'new' && !isLoaded) {
            setIsEditMode(true);
            const loadKnowledgebase = async () => {
                const response = await knowledgebaseService.getKnowledgebase(id);
                setKnowledgebaseSnapshot(response.data);
                setFormData({
                    name: response.data.name,
                    description: response.data.description,
                    documents: [],
                });
                setIsLoaded(true);
            };
            loadKnowledgebase();
        } else if (id === 'new') {
            setIsEditMode(false);
            setIsLoaded(true);
        } else {
            setIsLoaded(true);
        }
    }, [id, formData, isLoaded, demo]);

    return {
        isEditMode,
        isLoaded,
        isSaving,
        formData,
        i18n,
        handleReembedDocument,
        handleSubmit,
        handleChange,
        knowledgebaseSnapshot,
    };
}
